import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Play, Clock, Lock } from "lucide-react";

export default function CourseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);

  useEffect(() => {
    api.get(`/courses/${id}`).then(({ data }) => setCourse(data));
  }, [id]);

  if (!course) return <div className="p-10 font-mono-label text-muted-foreground">Loading…</div>;

  const locked = course.is_premium && !user?.is_premium;
  const freeLessonCount = locked ? (user?.has_referral_reward ? 2 : 1) : 9999;

  return (
    <div data-testid="course-detail-page">
      <div className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="font-mono-label text-primary flex items-center gap-3">
              {course.category} · {course.level}
              {course.is_premium && (
                <span className="font-mono-label bg-foreground text-background px-2 py-0.5" style={{ borderRadius: 2 }} data-testid="course-pro-badge">PRO</span>
              )}
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl mt-4 font-light leading-[0.95] tracking-tighter">
              {course.title}
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">{course.description}</p>
            <div className="mt-8 flex gap-6 font-mono-label text-muted-foreground">
              <span>{course.lessons?.length || 0} LESSONS</span>
              <span>{Math.round(course.duration_minutes / 60)} HOURS</span>
              <span>{course.level.toUpperCase()}</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={course.cover_image} alt={course.title} className="w-full h-80 object-cover" style={{ borderRadius: 2 }} />
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        {locked && (
          <div className="porcelain-card p-8 mb-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center" data-testid="premium-gate">
            <div className="md:col-span-2">
              <div className="font-mono-label text-primary flex items-center gap-2">
                <Lock className="w-3 h-3" /> Porcelain Pro
              </div>
              <h3 className="font-serif-display text-3xl md:text-4xl mt-3 leading-none tracking-tight">This is a Pro masterclass.</h3>
              <p className="mt-3 text-muted-foreground max-w-xl text-sm">
                Upgrade once to unlock all advanced posterior morphology and occlusion content. Lifetime access, no subscription.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Link to="/pricing" className="btn-oxblood text-sm inline-flex items-center gap-2" data-testid="premium-gate-cta">
                Unlock Pro — $99 <Lock className="w-3 h-3" />
              </Link>
            </div>
          </div>
        )}
        <div className="font-mono-label text-primary mb-6">Lesson Sequence</div>
        <div className="border-t border-border">
          {course.lessons?.map((l, idx) => {
            const lockThis = locked && idx >= freeLessonCount; // bonus-preview-aware
            const content = (
              <>
                <div className="col-span-2 md:col-span-1 font-mono text-2xl md:text-3xl font-light text-muted-foreground">
                  {String(l.order).padStart(2, "0")}
                </div>
                <div className="col-span-7 md:col-span-8">
                  <h3 className="font-serif-display text-2xl leading-none tracking-tight">{l.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{l.summary}</p>
                </div>
                <div className="col-span-3 flex items-center justify-end gap-4 font-mono-label text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {l.duration_minutes}M</span>
                  {lockThis ? <Lock className="w-4 h-4 text-muted-foreground" /> : <Play className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />}
                </div>
              </>
            );
            return lockThis ? (
              <Link
                to="/pricing"
                key={l.id}
                data-testid={`lesson-row-locked-${l.id}`}
                className="border-b border-border py-6 grid grid-cols-12 gap-6 items-center hover:bg-secondary/40 transition-colors group px-2 opacity-60"
              >
                {content}
              </Link>
            ) : (
              <Link
                to={`/lessons/${l.id}`}
                key={l.id}
                data-testid={`lesson-row-${l.id}`}
                className="border-b border-border py-6 grid grid-cols-12 gap-6 items-center hover:bg-secondary/40 transition-colors group px-2"
              >
                {content}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
